import { Box, Button, Table, Text, TextInput, Center, Stack, Menu } from '@mantine/core';
import { IconPlus, IconSearch, IconDots, IconTrash } from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { useFunds } from '../../hooks/useFunds';
import { useDeleteFund } from '../../hooks/useDeleteFund';
import { Fund } from '../../types';
import { AddFundModal } from './AddFundModal';
import CompanyPageLoadingSection from 'src/company/CompanyPageLoadingSection';
import CompanySectionLayout from 'src/company/CompanySectionLayout';
import { GenericErrorPage } from 'src/error/GenericErrorPage';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { formatCurrency } from 'src/common/currency';
import { formatPercentage } from 'src/common/percentage';
import { useCurrency } from 'src/common/hooks/useCurrency';

export default function FundsListPage() {
  const currency = useCurrency();
  const { funds, isLoading: isLoadingFunds, error, isError } = useFunds();
  const { deleteFund } = useDeleteFund();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [opened, { open, close }] = useDisclosure(false);

  const filteredFunds =
    funds?.filter(fund => fund.name.toLowerCase().includes(searchTerm.toLowerCase())) || [];

  const handleRowClick = (fund: Fund) => {
    navigate(`/company/funds/${fund.id}`);
  };

  const handleAddFund = () => {
    open();
  };

  const handleDeleteFund = async (fundId: string) => {
    try {
      await deleteFund(fundId);
    } catch (error) {
      notifyAndLogError(
        'Error',
        'We were not able to delete the fund. Please try again or contact support.',
        error,
      );
    }
  };

  if (isLoadingFunds) {
    return <CompanyPageLoadingSection title="Funds" />;
  }

  if (isError) {
    return (
      <CompanySectionLayout title="Funds">
        <GenericErrorPage
          error={error}
          title="Failed to load funds"
          text="There was an error loading the funds. Please try again."
        />
      </CompanySectionLayout>
    );
  }

  return (
    <CompanySectionLayout
      title="Funds"
      actions={
        funds.length > 0 ? (
          <Button
            variant="primary"
            size="md"
            leftSection={<IconPlus size={16} />}
            onClick={handleAddFund}
          >
            Add fund
          </Button>
        ) : undefined
      }
    >
      {funds.length > 0 && (
        <TextInput
          placeholder="Search funds..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          leftSection={<IconSearch size={16} />}
          variant="search"
        />
      )}

      {funds.length === 0 ? (
        <Center h={400}>
          <Stack align="center" gap="sm">
            <Text size="xxl" fw={FONT_WEIGHTS.semibold} c="gray.10">
              Keep your funds organised and connected
            </Text>
            <Text size="md" fw={FONT_WEIGHTS.medium} c="gray.8" ta="center">
              Keep your fund data up to date to generate accurate suitability reports in minutes.
            </Text>
            <Button
              variant="primary"
              size="md"
              leftSection={<IconPlus size={16} />}
              onClick={handleAddFund}
              mt="xxl"
            >
              Add fund
            </Button>
          </Stack>
        </Center>
      ) : (
        <Box>
          <Table variant="bare" verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>
                  <Text size="textXs" fw={FONT_WEIGHTS.semibold} c="gray.10">
                    Fund name
                  </Text>
                </Table.Th>
                <Table.Th>
                  <Text size="textXs" fw={FONT_WEIGHTS.semibold} c="gray.10">
                    Fund code
                  </Text>
                </Table.Th>
                <Table.Th>
                  <Text size="textXs" fw={FONT_WEIGHTS.semibold} c="gray.10">
                    Price per unit
                  </Text>
                </Table.Th>
                <Table.Th>
                  <Text size="textXs" fw={FONT_WEIGHTS.semibold} c="gray.10">
                    Annual management charge
                  </Text>
                </Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filteredFunds.map(fund => (
                <Table.Tr key={fund.id}>
                  <Table.Td onClick={() => handleRowClick(fund)} style={{ cursor: 'pointer' }}>
                    <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
                      {fund.name}
                    </Text>
                  </Table.Td>
                  <Table.Td onClick={() => handleRowClick(fund)} style={{ cursor: 'pointer' }}>
                    <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
                      {fund.code}
                    </Text>
                  </Table.Td>
                  <Table.Td onClick={() => handleRowClick(fund)} style={{ cursor: 'pointer' }}>
                    <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
                      {formatCurrency(fund.pricePerUnit, currency, { fractionDigits: 0 })}
                    </Text>
                  </Table.Td>
                  <Table.Td onClick={() => handleRowClick(fund)} style={{ cursor: 'pointer' }}>
                    <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
                      {fund.amcPercentage ? formatPercentage(fund.amcPercentage) : 'Unknown'}
                    </Text>
                  </Table.Td>
                  <Table.Td align="right">
                    <Menu
                      transitionProps={{ transition: 'pop-top-right' }}
                      position="bottom-end"
                      width={160}
                      withinPortal
                    >
                      <Menu.Target>
                        <IconDots
                          color="#475467"
                          stroke={2}
                          size={20}
                          style={{ cursor: 'pointer' }}
                        />
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item
                          leftSection={<IconTrash size={16} />}
                          color="red"
                          onClick={() => handleDeleteFund(fund.id)}
                        >
                          Delete
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Box>
      )}

      <AddFundModal opened={opened} onClose={close} />
    </CompanySectionLayout>
  );
}
